import type { DoNotDisturb } from '@crowdex/agent-contracts/api';
import { apiFetch } from '@crowdex/host-kit/renderer/http';

/*
 * Do not disturb (Settings › Notifications, D17): kept by the server, which only lists events
 * while it holds — no toast, no system notification (the Desktop's or a browser's push), no
 * call — here and on the linked Crowdex Cloud.
 */

export type DoNotDisturbChoice = DoNotDisturb['choice'];

export const DO_NOT_DISTURB_CHOICES: readonly { value: DoNotDisturbChoice; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'hour', label: 'For 1 hour' },
  { value: 'tomorrow', label: 'Until tomorrow' },
  { value: 'on', label: 'Until I turn it off' },
];

const PATH = '/api/notifications/do-not-disturb';

/** When a choice made at `now` ends, in the user's time zone: tomorrow is 8:00 the next morning. */
export function doNotDisturbUntil(choice: DoNotDisturbChoice, now: Date): Date | null {
  if (choice === 'hour') return new Date(now.getTime() + 60 * 60_000);
  if (choice === 'tomorrow') {
    const next = new Date(now);
    next.setDate(next.getDate() + 1);
    next.setHours(8, 0, 0, 0);
    return next;
  }
  return null;
}

export function loadDoNotDisturb(): Promise<DoNotDisturb> {
  return apiFetch<DoNotDisturb>(PATH, { cache: 'no-store' });
}

export function saveDoNotDisturb(
  choice: DoNotDisturbChoice,
  now = new Date(),
): Promise<DoNotDisturb> {
  const until = doNotDisturbUntil(choice, now);
  return apiFetch<DoNotDisturb>(PATH, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ choice, until: until?.toISOString() ?? null }),
  });
}

/** What the setting's row says under its title while it holds. */
export function doNotDisturbSummary(state: DoNotDisturb, locale?: string): string {
  if (state.choice === 'off') return 'Holds toasts, system notifications and calls; keeps the list';
  if (state.until === null) return 'On until you turn it off: events are only listed';
  const time = new Date(state.until).toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `On until ${time}: events are only listed`;
}
